import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Flex,
  Text,
  Button,
  Badge,
  Icon,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  type TextWeight,
  type TextLevel,
  type IDataTableCellRendererParams,
  type IDataTableRowClickedEvent,
} from '@kognitos/lattice';
import type { V1Run } from '@/generated/hooks/jeeves/models';
import type { V1BatchGetRunExecutionTimesResponseRunExecutionTimes } from '@/generated/hooks/bishop/models';
import type { IHeaderParams } from 'ag-grid-community';
import {
  formatTimestamp,
  formatDurationCompact,
  formatDisplayName,
} from '@/shared/utils';
import i18n from '@/shared/i18n';
import { getRunStateDisplay, getRunStateBadgeVariant } from '../utils';
import { isRunInTerminalState } from '@/modules/automation-run/utils/run-utils';
import {
  INVOCATION_SOURCE_BADGE_INFO,
  isEmailInvocation,
} from '@/shared/utils/run-utils';
import { extractOutputsValue } from '@/modules/outputs/utils';
import { ValueList } from '@/shared/components/ValueList';
import { RunTableOutputPreview } from '@/modules/outputs/components/RunTableOutputPreview';
import type { TResolvedOutputs, TResolvedValue } from '@/modules/outputs/types';
import {
  hasRunContentSearchDetails,
  type TRunContentMatches,
} from '../content-search';
import { useRunTriggerDetail } from './RunTriggerDetail';
import { RunContentSearchDetails } from './RunContentSearchDetails';
import {
  TriggerDetailHoverCard,
  TriggerDetailText,
} from './RunTriggerPopovers';

/**
 * Parses a protobuf duration string (e.g. "2.659352s") to seconds.
 *
 * @param duration - Duration string in format "Xs" where X is seconds
 * @returns Parsed seconds, or NaN if invalid
 */
function parseDurationSeconds(duration: string | undefined): number {
  if (!duration) return NaN;
  const match = duration.match(/^(-?\d+\.?\d*)s?$/);
  if (!match) return NaN;
  return parseFloat(match[1]);
}

/**
 * Formats an execution duration for display: "<1 sec" for sub-second values,
 * otherwise delegates to formatDurationCompact.
 *
 * @param durationStr - Protobuf duration string (e.g. "2.659352s")
 * @returns Human-readable duration string
 */
export function formatExecutionDuration(durationStr: string): string {
  const seconds = parseDurationSeconds(durationStr);
  if (isNaN(seconds) || seconds < 0) return formatDurationCompact(durationStr);
  if (seconds < 1) {
    return i18n.t('automation.runs.duration.lessThanOneSec', {
      ns: 'automation',
    });
  }
  return formatDurationCompact(durationStr);
}

/**
 * Renders tooltip content showing execution time and guidance time for
 * a terminal run's time column.
 *
 * Guidance time comes straight from the API's `exception_duration` (the summed
 * time the run spent awaiting exception resolution) rather than being derived
 * from the start/end timestamps.
 *
 * @param props - Component props
 * @param props.executionDuration - Protobuf duration string for execution time
 * @param props.exceptionDuration - Protobuf duration string for guidance time
 */
export function TimeTooltipContent({
  executionDuration,
  exceptionDuration,
}: {
  executionDuration: string;
  exceptionDuration?: string;
}) {
  const execFormatted = formatExecutionDuration(executionDuration);

  const guidanceSeconds = parseDurationSeconds(exceptionDuration);
  const TOLERANCE_SECONDS = 1;
  const hasGuidanceTime =
    !isNaN(guidanceSeconds) && guidanceSeconds > TOLERANCE_SECONDS;
  const guidanceFormatted = hasGuidanceTime
    ? formatDurationCompact(`${Math.round(guidanceSeconds)}s`)
    : '';

  return (
    <Flex direction="column" gap={1}>
      <Text level="xSmall">
        {i18n.t('automation.runs.tooltip.executionTime', { ns: 'automation' })}:{' '}
        {execFormatted}
      </Text>
      {hasGuidanceTime && (
        <Text level="xSmall">
          {i18n.t('automation.runs.tooltip.guidanceTime', {
            ns: 'automation',
          })}
          : {guidanceFormatted}
        </Text>
      )}
    </Flex>
  );
}

/**
 * Muted "-" placeholder shown for empty cell values: left-aligned (matching
 * the column's left-aligned values) and vertically centered in the cell.
 *
 * @returns Placeholder cell content
 */
export const EmptyCellValue = () => (
  <Flex align="start">
    <Text level="small" color="muted">
      {i18n.t('automation.runs.emptyValue', { ns: 'automation' })}
    </Text>
  </Flex>
);

/**
 * Creates a cell renderer for the unified "Time" column: the run's start
 * date on top and "<start time> · <duration>" below, where the duration is
 * the execution time for terminal runs. A tooltip breaks the duration down
 * into execution and guidance time when available. Under them, the run's
 * file-content search details, when it has any.
 *
 * @param executionTimesMap - Map of run resource names to execution time data
 * @param contentMatches - ListRuns `content_matches` of the loaded runs, by run name
 * @returns Cell renderer function
 */
export const createTimeCellRenderer = (
  executionTimesMap: V1BatchGetRunExecutionTimesResponseRunExecutionTimes,
  contentMatches: TRunContentMatches = {},
) => {
  return ({ data }: IDataTableCellRendererParams<V1Run>) => {
    if (!data?.create_time) {
      return '-';
    }

    const date = formatTimestamp(data.create_time, 'MMMM D, YYYY');
    const time = formatTimestamp(data.create_time, 'h:mm A');

    const isTerminal = isRunInTerminalState(data.state);
    const execTime = data.name ? executionTimesMap[data.name] : undefined;
    const hasDuration = isTerminal && Boolean(execTime?.execution_duration);
    const durationLabel = hasDuration
      ? formatExecutionDuration(execTime!.execution_duration!)
      : '';

    const cellContent = (
      <Flex direction="column" gap={1}>
        <Text level="small">{date}</Text>
        <Text level="xSmall" color="muted">
          {durationLabel ? `${time} · ${durationLabel}` : time}
        </Text>
      </Flex>
    );

    const timeContent = hasDuration ? (
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div className="cursor-default">{cellContent}</div>
        </TooltipTrigger>
        <TooltipContent side="left">
          <TimeTooltipContent
            executionDuration={execTime!.execution_duration!}
            exceptionDuration={execTime?.exception_duration}
          />
        </TooltipContent>
      </Tooltip>
    ) : (
      cellContent
    );

    const contentMatch = data.name ? contentMatches[data.name] : undefined;
    if (!hasRunContentSearchDetails(data, contentMatch)) return timeContent;

    return (
      <Flex direction="column" gap={2} width="full" className="min-w-0">
        {timeContent}
        <RunContentSearchDetails run={data} match={contentMatch} />
      </Flex>
    );
  };
};

/**
 * Renders the "Start time" column: the run's start date on top and the start
 * time below. The execution duration is intentionally NOT shown here — it
 * belongs to the End time column.
 *
 * @param params - Cell renderer params
 * @returns Start date/time cell, or "-" when no start time
 */
export const StartTimeCellRenderer = ({
  data,
}: IDataTableCellRendererParams<V1Run>) => {
  if (!data?.create_time) {
    return <EmptyCellValue />;
  }

  const date = formatTimestamp(data.create_time, 'MMMM D, YYYY');
  const time = formatTimestamp(data.create_time, 'h:mm A');

  return (
    <Flex direction="column" gap={1}>
      <Text level="small">{date}</Text>
      <Text level="xSmall" color="muted">
        {time}
      </Text>
    </Flex>
  );
};

/**
 * Creates a cell renderer for the "End time" column: the run's end date on top
 * and "<end time> · <duration>" below, where the duration is the total
 * duration for terminal runs (wall-clock time including guidance, not just
 * net execution time). Non-terminal runs (no end time yet) render "-". A
 * tooltip breaks the duration into execution and guidance time when available.
 *
 * @param executionTimesMap - Map of run resource names to execution time data
 * @returns Cell renderer function
 */
export const createEndTimeCellRenderer = (
  executionTimesMap: V1BatchGetRunExecutionTimesResponseRunExecutionTimes,
) => {
  return ({ data }: IDataTableCellRendererParams<V1Run>) => {
    const endTimeIso = data?.state?.update_time;
    if (!data || !isRunInTerminalState(data.state) || !endTimeIso) {
      return <EmptyCellValue />;
    }

    const date = formatTimestamp(endTimeIso, 'MMMM D, YYYY');
    const time = formatTimestamp(endTimeIso, 'h:mm A');

    const execTime = data.name ? executionTimesMap[data.name] : undefined;
    // Show total duration (wall-clock, including guidance time) rather than
    // net execution time; fall back to execution time when total is absent.
    const displayDuration =
      execTime?.total_duration ?? execTime?.execution_duration;
    const durationLabel = displayDuration
      ? formatExecutionDuration(displayDuration)
      : '';

    const cellContent = (
      <Flex direction="column" gap={1}>
        <Text level="small">{date}</Text>
        <Text level="xSmall" color="muted">
          {durationLabel ? `${time} · ${durationLabel}` : time}
        </Text>
      </Flex>
    );

    // The tooltip breaks the time down into execution vs. guidance time, so it
    // needs execution_duration specifically — not the total shown in the cell.
    if (execTime?.execution_duration) {
      return (
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <div className="cursor-default">{cellContent}</div>
          </TooltipTrigger>
          <TooltipContent side="left">
            <TimeTooltipContent
              executionDuration={execTime.execution_duration}
              exceptionDuration={execTime.exception_duration}
            />
          </TooltipContent>
        </Tooltip>
      );
    }

    return cellContent;
  };
};

/**
 * Component that displays run state with appropriate text and color.
 *
 * @param state - The run state object from V1Run
 * @param level - Optional text level (defaults to 'small')
 * @param weight - Optional text weight (defaults to 'semibold')
 */
export const RunStateDisplay = ({
  state,
  level = 'small',
  weight = 'semibold',
}: {
  state: V1Run['state'];
  level?: TextLevel;
  weight?: TextWeight;
}) => {
  const { text, color } = getRunStateDisplay(state);

  return (
    <Text level={level} weight={weight} className={color}>
      {text}
    </Text>
  );
};

export const StatusCellRenderer = ({
  data,
}: IDataTableCellRendererParams<V1Run>) => {
  const { text } = getRunStateDisplay(data?.state);
  const variant = getRunStateBadgeVariant(data?.state);

  return (
    <Flex align="start">
      <Badge variant={variant}>{text}</Badge>
    </Flex>
  );
};

/**
 * Rows the inputs cell shows before ValueList's "Show N more" link; two rows
 * plus the link fill the runs table's fixed row height.
 */
const MAX_VISIBLE_INPUT_ROWS = 2;

/**
 * Cell content for the inputs column: the run log's name/value list, capped at
 * {@link MAX_VISIBLE_INPUT_ROWS}. Expanding grows the row (the grid uses
 * autoHeight).
 *
 * @param data - Run data (may be undefined)
 */
function InputsCell({ data }: { data: V1Run | undefined }) {
  const userInputs = data?.user_inputs;
  if (!userInputs || Object.keys(userInputs).length === 0) {
    return <EmptyCellValue />;
  }

  const resolved = extractOutputsValue(
    userInputs as Parameters<typeof extractOutputsValue>[0],
  );
  const entries = Object.entries(resolved).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  return (
    <Flex align="start" paddingY={1} width="full" className="min-w-0">
      <ValueList
        entries={entries}
        maxRows={MAX_VISIBLE_INPUT_ROWS}
        fitContent
        ariaLabel={i18n.t('automation.runs.inputs', { ns: 'automation' })}
      />
    </Flex>
  );
}

/**
 * Creates a cell renderer for the inputs column.
 *
 * @returns Cell renderer function
 */
export const createInputsCellRenderer = () => {
  return ({ data }: IDataTableCellRendererParams<V1Run>) => (
    <InputsCell data={data} />
  );
};

/** @deprecated Use createInputsCellRenderer() instead */
export const InputsCellRenderer = createInputsCellRenderer();

/**
 * Header for output columns: the output name, with a tooltip when truncated.
 *
 * @param props - AG Grid header params (uses `displayName`)
 * @returns Output column header content
 */
type IOutputColumnHeaderProps = IHeaderParams;

export const OutputColumnHeader = ({
  displayName,
}: IOutputColumnHeaderProps) => (
  <Text
    as="span"
    level="small"
    color="muted"
    weight="medium"
    ellipsis={{ tooltip: true }}
    className="min-w-0 flex-1"
  >
    {displayName}
  </Text>
);

/**
 * The "-" an output cell shows when a run has no value for it. Output cells
 * have no cell padding (the preview owns its inset), so this supplies the
 * same 12px the preview card and the header text start at.
 */
function EmptyOutputValue() {
  return (
    <Flex padding={3}>
      <EmptyCellValue />
    </Flex>
  );
}

/**
 * Cell for the single "Outputs" column of an automation that has no outputs.
 * A completed run says it returned none; a run that hasn't finished (or never
 * got far enough) shows "-", since it could still produce something.
 *
 * @param props - AG Grid cell params (uses `data`)
 * @returns "No outputs" for a completed run, otherwise "-"
 */
export const NoOutputsCellRenderer = ({
  data,
}: IDataTableCellRendererParams<V1Run>) =>
  data?.state?.completed ? (
    <Flex align="start">
      <Text level="small" color="muted">
        {i18n.t('automation.runs.outputs.none', { ns: 'automation' })}
      </Text>
    </Flex>
  ) : (
    <EmptyCellValue />
  );

function OutputCell({
  data,
  outputName,
  onOpen,
  capToDefaultWidth = false,
}: {
  data: V1Run | undefined;
  outputName: string;
  /** Keep the preview at the default column width; false once dragged. */
  capToDefaultWidth?: boolean;
  onOpen?: (
    name: string,
    value: TResolvedValue,
    outputs: TResolvedOutputs,
  ) => void;
}) {
  const emptyOutput = <EmptyOutputValue />;

  const outputs = data?.state?.completed?.outputs;
  if (!outputs) return emptyOutput;
  const resolved = extractOutputsValue(
    outputs as Parameters<typeof extractOutputsValue>[0],
  );
  const value = resolved[outputName];
  if (value === undefined) return emptyOutput;

  return (
    <RunTableOutputPreview
      name={formatDisplayName(outputName)}
      value={value}
      onOpen={onOpen ? () => onOpen(outputName, value, resolved) : undefined}
      // 200px is the default output column width (OUTPUT_COLUMN_MIN_WIDTH):
      // a column stretched to fill spare table width keeps a default-size
      // card, so one output looks like one of many. Dragged columns fill.
      className={capToDefaultWidth ? 'max-w-[200px]' : undefined}
    />
  );
}

/**
 * Creates a cell renderer for a single output column.
 * Shows the output's preview; "-" when the run has no value for it.
 *
 * @param outputName - The output key/name
 * @param onOpen - Opens the output in the preview layover
 * @param options.capToDefaultWidth - Keep the preview at the default column
 *   width even when the column stretches; false for a dragged column
 * @returns Cell renderer function
 */
export const createOutputCellRenderer = (
  outputName: string,
  onOpen?: (
    name: string,
    value: TResolvedValue,
    outputs: TResolvedOutputs,
  ) => void,
  { capToDefaultWidth = false }: { capToDefaultWidth?: boolean } = {},
) => {
  return ({ data }: IDataTableCellRendererParams<V1Run>) => (
    <OutputCell
      data={data}
      outputName={outputName}
      onOpen={onOpen}
      capToDefaultWidth={capToDefaultWidth}
    />
  );
};

/**
 * Confirmation dialog shown before archiving a run.
 *
 * @param props.open - Whether the dialog is open
 * @param props.onOpenChange - Callback when the dialog open state changes
 * @param props.onConfirm - Callback when archiving is confirmed
 */
export function ArchiveRunAlertDialog({
  open,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="archive-run-alert-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {i18n.t('automation.runs.archiveConfirm.title', {
              ns: 'automation',
            })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {i18n.t('automation.runs.archiveConfirm.description', {
              ns: 'automation',
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {i18n.t('common.action.cancel', { ns: 'common' })}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            data-testid="archive-run-confirm-button"
          >
            {i18n.t('common.action.archive', { ns: 'common' })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Creates a cell renderer for the actions column with a "View Run" button and
 * a dropdown menu containing "Archive run", which opens a confirmation dialog.
 *
 * @param onViewRun - Callback to navigate to run details
 * @param onArchiveRun - Callback to archive a run
 * @param showMenuButton - Whether to render the actions (archive) menu
 * @param isArchiving - Whether an archive operation is in progress
 * @returns A cell renderer function
 */
export const createActionsCellRenderer = (
  onViewRun: (event: IDataTableRowClickedEvent<V1Run>) => void,
  onArchiveRun: (run: V1Run) => void,
  showMenuButton: boolean,
  isArchiving: boolean,
) => {
  return ({ data }: IDataTableCellRendererParams<V1Run>) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const handleViewClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (data) {
        onViewRun({ data } as IDataTableRowClickedEvent<V1Run>);
      }
    };

    const handleArchiveConfirm = () => {
      if (data?.name) {
        setIsDialogOpen(false);
        onArchiveRun(data);
      }
    };

    return (
      <Flex align="start" gap={2}>
        <Button variant="outline" size="sm" onClick={handleViewClick}>
          {i18n.t('automation.runs.viewRun', { ns: 'automation' })}
        </Button>
        {showMenuButton && (
          // modal={false} so opening the menu doesn't engage Radix's scroll
          // lock, which pads <body> and shifts the page's left edge.
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon-sm"
                disabled={isArchiving}
                onClick={e => e.stopPropagation()}
                aria-label={i18n.t('automation.runs.archiveRun', {
                  ns: 'automation',
                })}
                data-testid="run-actions-menu-trigger"
              >
                <Icon type="Archive" size="sm" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
              <DropdownMenuItem
                onClick={() => setIsDialogOpen(true)}
                className="text-destructive"
                data-testid="archive-run-menu-item"
              >
                <Icon type="Archive" size="xs" />
                {i18n.t('automation.runs.archiveRun', { ns: 'automation' })}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <ArchiveRunAlertDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          onConfirm={handleArchiveConfirm}
        />
      </Flex>
    );
  };
};

export const TriggerCellRenderer = ({
  data,
}: IDataTableCellRendererParams<V1Run>) => {
  const { label, popover, isWidePopover } = useRunTriggerDetail(data);

  const source = data?.invocation_details?.invocation_source;
  const badgeInfo = source ? INVOCATION_SOURCE_BADGE_INFO[source] : undefined;
  if (!source || !badgeInfo) return <EmptyCellValue />;

  const labelKey = isEmailInvocation(data?.invocation_details)
    ? 'automation.run.invocationSource.email'
    : badgeInfo.labelKey;

  const badge = (
    // Lattice gates badge hover styles behind [a&], so a hoverable badge has to
    // opt into the outline variant's own hover treatment.
    <Badge
      variant="outline"
      className={
        popover
          ? 'transition-colors hover:bg-accent hover:text-accent-foreground'
          : undefined
      }
    >
      <Flex gap={1.5} align="center">
        <Icon type={badgeInfo.icon} size="xs" />
        {i18n.t(labelKey, { ns: 'automation' })}
      </Flex>
    </Badge>
  );

  return (
    <Flex
      direction="column"
      gap={1}
      align="start"
      width="full"
      className="min-w-0"
    >
      {popover ? (
        <TriggerDetailHoverCard content={popover} wide={isWidePopover}>
          {badge}
        </TriggerDetailHoverCard>
      ) : (
        badge
      )}
      {label && <TriggerDetailText>{label}</TriggerDetailText>}
    </Flex>
  );
};

/**
 * Renders the stage version as plain text with a "v" prefix.
 *
 * @param params - Cell renderer params
 * @returns Version text or null
 */
export const VersionCellRenderer = ({
  data,
}: IDataTableCellRendererParams<V1Run>) => {
  const version = data?.stage_version;
  if (!version) return <EmptyCellValue />;

  return (
    <Flex align="start">
      <Text level="small">v{version}</Text>
    </Flex>
  );
};
